import { Fragment, useCallback, useContext, useRef } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  Account,
  AccountCollection,
  AccountData,
  ClientCollection,
  Connection,
  Connector,
  getHorizon,
} from "wealthmode-frontend/backend"
import { HStack, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoney, formatPercentage } from "wealthmode-frontend/util"

import DataTable from "@/components/DataTable"
import IDLink from "@/components/IDLink"
import { IRRValues } from "@/components/widgets"
import { BackendContext } from "@/context"
import { confidenceColor } from "@/lib/irr"
import {
  ACCOUNT_DATA_LINKS,
  CONNECTION_STATUS_COLORS,
  CONNECTION_STATUS_LABELS,
  SYNC_STATE_COLORS,
  SYNC_STATE_LABELS,
} from "@/lib/labels"

export default function Accounts() {
  const { client_id } = useLocalSearchParams<{ client_id: string }>()
  const backend = useContext(BackendContext)

  // Connection status is derived from every account on that connection, not just the row's own.
  const loaded = useRef<Account[]>([])

  const load = useCallback(async () => {
    var clients = backend.collection("clients") as ClientCollection
    var client = clients.model(client_id)
    await client.fetch({ reload: true })

    var accounts = client.link("accounts")?.resolve() as AccountCollection | undefined
    if (!accounts) {
      return undefined
    }

    await accounts.fetch({ reload: true })
    loaded.current = accounts.models

    return accounts
  }, [backend, client_id])

  function connectionAccounts(connection: Connection): Account[] {
    return loaded.current.filter((account) => account.link("connection")?.resolve().id === connection.id)
  }

  return (
    <DataTable
      title={"Accounts"}
      load={load}
      header={[
        { id: "id", title: "ID", shrink: true },
        { id: "name", title: "Name" },
        { id: "connector", title: "Connector" },
        { id: "mask", title: "Mask", shrink: true },
        { id: "type", title: "Type", shrink: true },
        { id: "subtype", title: "Sub Type", shrink: true },
        { id: "variant", title: "Variant", shrink: true },
        { id: "balance", title: "Balance", shrink: true },
        { id: "blended_cagr", title: "Blended 10Y CAGR", shrink: true },
        { id: "irr", title: "IRR", shrink: true },
        { id: "value_confidence", title: "Confidence", shrink: true },
        { id: "connection_status", title: "Connection", shrink: true },
        { id: "status", title: "Status", shrink: true },
        { id: "links", title: "Links", center: true, noTruncate: true },
      ]}
      render={(model: Account, field: string) => {
        switch (field) {
          case "id":
            return (
              <IDLink
                href={`/clients/${client_id}/accounts/${model.attributes.id}`}
                id={model.attributes.id}
              />
            )

          case "links": {
            const data = model.embed("data") as AccountData | null
            const base = `/clients/${client_id}/accounts/${model.attributes.id}`
            const links = ACCOUNT_DATA_LINKS.filter(({ rel }) => data?.link(rel)).map(({ rel, label }) => ({
              rel,
              href: `${base}/${rel}`,
              label,
            }))

            if (!model.isInvestment) {
              return <LinkRow links={links} />
            }

            return (
              <VStack className="items-center gap-1">
                {INVESTMENT_LINK_ROWS.map((rels, i) => (
                  <LinkRow
                    key={i}
                    links={rels.flatMap((rel) => links.filter((link) => link.rel === rel))}
                  />
                ))}
              </VStack>
            )
          }

          case "connector":
            const connector = model.embed("connector") as Connector | null
            return <Text className="text-foreground-secondary">{connector?.attributes.name ?? ""}</Text>

          case "variant":
            return <Text className="text-foreground-secondary">{model.attributes.variant ?? "--"}</Text>

          case "balance":
            return <Text className="text-foreground-secondary">{formatMoney(model.attributes.balance)}</Text>

          case "blended_cagr": {
            const blended = model.attributes.blended_cagr

            return (
              <Text className="text-foreground-secondary">
                {blended == null ? "--" : formatPercentage(getHorizon(blended, 10))}
              </Text>
            )
          }

          // Served to admin tokens only. A depository account is not invested, so the market says
          // nothing about it.
          case "irr":
            return (
              <IRRValues
                irr={model.attributes.irr}
                plain={model.isDepository}
              />
            )

          // How far the value series the IRRs are measured from can be trusted.
          case "value_confidence": {
            const confidence = model.attributes.value_confidence

            return (
              <Text className={confidenceColor(confidence)}>
                {confidence == null ? "--" : formatPercentage(confidence)}
              </Text>
            )
          }

          case "connection_status": {
            const connection = model.embed("connection") as Connection | null
            if (connection == null) {
              return <Text className="text-foreground-secondary">--</Text>
            }

            const { status } = connection.status(connectionAccounts(connection))

            return <Text className={CONNECTION_STATUS_COLORS[status]}>{CONNECTION_STATUS_LABELS[status]}</Text>
          }

          case "status": {
            const data = model.embed("data") as AccountData | null
            if (data == null) {
              return <Text className="text-foreground-secondary">--</Text>
            }

            const state = data.syncState()

            return <Text className={SYNC_STATE_COLORS[state]}>{SYNC_STATE_LABELS[state]}</Text>
          }
        }
        return <Text className="text-foreground-secondary">{model.get(field)}</Text>
      }}
    />
  )
}

// An investment account has too many links for one line.
const INVESTMENT_LINK_ROWS: string[][] = [
  ["investment_holdings", "investment_transactions", "recurring_investment_transactions", "estimated_account_values"],
  ["contributions", "recurring_contributions", "dividends"],
]

function LinkRow({ links }: { links: { href: string; label: string }[] }) {
  if (links.length === 0) {
    return null
  }

  return (
    <HStack className="items-center gap-1">
      {links.map((link, i) => (
        <Fragment key={link.href}>
          {i > 0 && <Text className="text-foreground-tertiary">•</Text>}
          <Link href={link.href}>
            <Text className="text-markdown-link hover:opacity-80 underline whitespace-nowrap">{link.label}</Text>
          </Link>
        </Fragment>
      ))}
    </HStack>
  )
}
